// NY PROHIBITED-CONCLUSION PHRASE TABLE — DATA (docs/KNOWLEDGE-IMPORT-MANIFEST.md K-23).
//
// Source: docs/authority/ny/NY-AI-SCOPE-POLICY.md §4 (P-01 … P-14, each with its New York basis and
// its allowed replacement) and docs/authority/ny/NY-REPORT-AUTHORITY.md §5 (the client-facing
// forbidden / allowed phrase lists behind RP-02) plus the CK-W7 class rule of
// docs/authority/ny/NY-CHECKLIST-COMPLIANCE-GAP.md §3 (numeric code thresholds and
// "required / should have" placement claims). Frozen plan §14.
//
// Two surfaces, one table (`appliesTo`):
//   ai      scanned on the AI's report-bound fields — observation, recommendation, title, headline —
//           inside the Scope Gate (step 4). A hit removes THAT SENTENCE and keeps the rest; it is
//           never a paragraph deleter (plan §14).
//   report  scanned on the inspector's own printed text at Final Review as a NON-BLOCKING reminder
//           (RP-02; OWNER-CONTRACT §8.9 "提醒不阻断"). Report generation is never blocked by it.
// Inspector-facing guidance — `guidance`, `checkSteps`, `concern`, checklist hints, the private item
// note and the AI scan note — is NEVER scanned: building-code knowledge is explicitly allowed there
// (AI policy §4 closing paragraph).
//
// Pattern discipline: every pattern is anchored on the conclusion, not on an ordinary word. "code"
// alone, "R-value", "structural engineer", "safety concern", "may contain asbestos", "per the
// manufacturer's installation instructions" and the other §14 positive fixtures must all survive —
// tests/domain/wordingGuard.test.ts pins that in both languages.
//
// One English gap is recorded deliberately: the two-word P-02 forms built on the building-approval
// noun ("… required", "without a …") are NOT in this table, because the Owner-Test product guard
// (tests/guards/ownerTestProduct.guard.test.ts) forbids that noun anywhere under src/. "unpermitted",
// the illegal-use forms and the Chinese 许可 / 报建 forms carry P-02 instead; the gap is listed in the
// Round 4 report for the Owner.
export type ProhibitedPhraseId =
  | 'P-01'
  | 'P-02'
  | 'P-03'
  | 'P-04'
  | 'P-05'
  | 'P-06'
  | 'P-07'
  | 'P-08'
  | 'P-09'
  | 'P-10'
  | 'P-11'
  | 'P-12'
  | 'P-13'
  | 'P-14'
  | 'P-15'
  | 'P-16';

export type WordingSurface = 'ai' | 'report';

export interface ProhibitedPhrase {
  id: ProhibitedPhraseId;
  // Short English description of the prohibited conclusion (audit trail, never user-facing copy).
  conclusion: string;
  ruleIds: readonly string[];
  sectionRefs: readonly string[];
  appliesTo: readonly WordingSurface[];
  patterns: { en: readonly RegExp[]; zh: readonly RegExp[] };
}

const BOTH: readonly WordingSurface[] = Object.freeze(['ai', 'report']);
const AI_ONLY: readonly WordingSurface[] = Object.freeze(['ai']);

function phrase(
  id: ProhibitedPhraseId,
  conclusion: string,
  ruleIds: readonly string[],
  sectionRefs: readonly string[],
  appliesTo: readonly WordingSurface[],
  patterns: { en: readonly RegExp[]; zh: readonly RegExp[] }
): ProhibitedPhrase {
  return Object.freeze({
    id,
    conclusion,
    ruleIds: Object.freeze([...ruleIds]),
    sectionRefs: Object.freeze([...sectionRefs]),
    appliesTo: Object.freeze([...appliesTo]),
    patterns: Object.freeze({ en: Object.freeze([...patterns.en]), zh: Object.freeze([...patterns.zh]) })
  });
}

export const NY_PROHIBITED_PHRASES: readonly ProhibitedPhrase[] = Object.freeze([
  phrase(
    'P-01',
    'Code / ordinance / regulation compliance or violation, incl. code citations and code-derived thresholds',
    ['NY-PRO-02', 'NY-CODE-01', 'NY-CODE-02'],
    ['197-4.4(d)', '197-5.16(e)'],
    BOTH,
    {
      en: [
        /\bcode\s+violations?\b/i,
        /\bviolat(?:e|es|ed|ing|ion\s+of)\s+(?:the\s+)?(?:building|electrical|plumbing|fire|mechanical|energy|local|state|current)?\s*codes?\b/i,
        /\bnot\s+(?:up\s+)?to\s+code\b/i,
        /\bup\s+to\s+code\b/i,
        /\bcode[\s-]compliant\b/i,
        /\bcode\s+compliance\b/i,
        /\bcompl(?:y|ies|ied|iance)\s+with\s+(?:the\s+)?(?:\w+\s+){0,2}codes?\b/i,
        /\bmeets?\s+(?:the\s+)?(?:current\s+|local\s+|building\s+|electrical\s+|plumbing\s+|fire\s+)?codes?\b/i,
        /\bcodes?\s+requires?\b/i,
        /\brequired\s+by\s+(?:the\s+)?(?:\w+\s+)?(?:code|codes|nec|irc|ibc|upc|ipc|nfpa)\b/i,
        /\b(?:nec|irc|ibc|upc|ipc|nfpa)\b\s*(?:§|section|article|chapter|table)?\s*\d/i,
        /\b(?:per|under|according\s+to)\s+(?:the\s+)?(?:nec|irc|ibc|upc|ipc|nfpa)\b/i,
        /\b(?:building|electrical|plumbing|fire)\s+code\s+(?:requires?|requirements?|violations?)\b/i,
        // CK-W7: code-derived thresholds and placement claims in client text. A measurement the
        // inspector actually took ("the gap measured 6 inches") is an observation and survives; a
        // requirement ("must be at least 36 inches") is a code determination and does not.
        /\b(?:must|shall|should|needs?\s+to)\s+be\s+(?:at\s+least|a\s+minimum\s+of|a\s+maximum\s+of|no\s+(?:less|more)\s+than)?\s*\d+(?:\.\d+)?\s*(?:inch|inches|in\.|foot|feet|ft\.?|cm|mm|metres?|meters?|amps?|volts?)\b/i,
        /\b(?:minimum|maximum|required)\s+(?:height|width|size|clearance|spacing|distance|rise|run|headroom)\b/i,
        /\b(?:gfci|afci|smoke\s+(?:alarms?|detectors?)|carbon\s+monoxide\s+(?:alarms?|detectors?)|handrails?|guardrails?|guards)\s+(?:is|are|was|were)\s+required\b/i,
        /\bshould\s+have\s+(?:a\s+|an\s+)?(?:gfci|afci|smoke|carbon\s+monoxide|handrail|guardrail|guard|fire[\s-]rated)/i,
        /\bfire[\s-]rated\s+(?:door|assembly|separation)?\s*(?:is\s+)?(?:required|compliant|compliance)\b/i,
        /\brequired\s+(?:locations?|height|clearance|spacing|distance)\b/i
      ],
      zh: [
        /违反[^，。；；,.;\s]{0,8}?(?:规范|法规|条例|规定|法律|法令)/,
        /(?:不)?符合[^，。；;,.\s]{0,8}?(?:规范|法规|条例|建筑法|电气法规|消防法规)/,
        /(?:规范|法规|条例)(?:要求|规定|禁止)/,
        /(?:违规|违章|不合规|合规性判定)/,
        /(?:达到|满足|合乎)(?:现行)?(?:建筑|电气|消防)?规范/,
        /\b(?:nec|irc|ibc|upc|ipc|nfpa)\b/i,
        /(?:应|应当|必须|须|规定)(?:不少于|不小于|不低于|至少|不超过|最多|为)\s*\d+(?:\.\d+)?\s*(?:英寸|寸|英尺|尺|厘米|公分|毫米|米|安培|安|伏)/,
        /(?:最小|最大|规定)(?:高度|宽度|尺寸|间距|净空|距离|净高)/,
        /(?:应|应当|必须|须)(?:设有|安装|配备|为|是|采用|使用)?\s*(?:GFCI|AFCI|漏电保护|烟感|烟雾报警|一氧化碳报警|扶手|护栏|自闭|防火门)/i
      ]
    }
  ),
  phrase(
    'P-02',
    'Legality of use: unapproved work, illegal apartment / conversion, zoning',
    ['NY-PRO-02', 'NY-PRO-03'],
    ['197-4.4(c)', '197-4.4(d)'],
    BOTH,
    {
      en: [
        /\bunpermitted\b/i,
        /\bunapproved\s+(?:work|addition|conversion|alteration)\b/i,
        /\billegal(?:ly)?\s+(?:apartment|unit|conversion|converted|addition|dwelling|bedroom|basement|rental|occupancy)\b/i,
        /\bzoning\s+(?:violation|compliance|non-?compliance|issue)s?\b/i,
        /\bcertificate\s+of\s+occupancy\b/i,
        /\bnot\s+legally\s+(?:allowed|permissible|habitable)\b/i
      ],
      zh: [
        /违建|违章建筑|违章搭建|违规搭建/,
        /(?:非法|违法)(?:改建|加建|搭建|出租|分租|公寓|单元|住宅|地下室|居住)/,
        /未(?:经|取得|办理)[^，。；;,.\s]{0,6}?(?:许可|批准|审批|报建)/,
        /无(?:施工|建筑)?许可(?:证)?/,
        /(?:分区|用途)(?:违规|违章|不符)|不符合(?:分区|用途)规定/,
        /入住许可证|使用许可证/
      ]
    }
  ),
  phrase(
    'P-03',
    'Boundary, encroachment, easement or ownership determination',
    ['NY-PRO-03'],
    ['197-4.4(c)'],
    BOTH,
    {
      en: [/\bencroach(?:es|ed|ing|ment|ments)\b/i, /\bproperty\s+lines?\b/i, /\beasements?\b/i, /\blot\s+lines?\b/i, /\bsurvey\s+line\b/i],
      zh: [
        /越界|侵占(?:邻|他人|相邻)/,
        /地役权/,
        // 「地界」只有在被断言时才是判定：「本次验屋未判定……地界位置」是允许的免责措辞。
        /(?:地界线?|产权(?:边)?界|用地红线)[^，。；;,.！!？?]{0,6}?(?:为|是|在|属于|位于)/
      ]
    }
  ),
  phrase(
    'P-04',
    'Market value, marketability or advisability of the purchase',
    ['NY-PRO-04'],
    ['197-4.4(e)', '197-5.16(k)(8)'],
    BOTH,
    {
      en: [
        /\bmarket\s+value\b/i,
        /\bresale\s+value\b/i,
        /\bmarketability\b/i,
        /\bproperty\s+value\b/i,
        /\bappraised?\s+value\b/i,
        /\b(?:should|should\s+not|shouldn['’]t|do\s+not|don['’]t)\s+(?:buy|purchase)\b/i,
        /\brecommend\s+(?:against\s+)?(?:buying|purchasing|the\s+purchase)\b/i,
        /\bwalk\s+away\s+from\s+(?:this\s+)?(?:house|property|deal)\b/i
      ],
      zh: [/市场价(?:值|格)|转售价值|房屋价值|估价/, /(?:建议|不建议|劝)(?:客户|买家)?(?:购买|买入|不要买|放弃)/, /值不值得买/]
    }
  ),
  phrase(
    'P-05',
    'Engineering / architectural conclusion: soundness, adequacy, occupancy safety, capacity',
    ['NY-PRO-01', 'NY-ANC-03'],
    ['§444-g', '§444-j', '197-4.9(c)', '197-5.16(k)(3)'],
    BOTH,
    {
      en: [
        /\bstructurally\s+(?:sound|unsound|adequate|inadequate|safe|unsafe|deficient|stable|unstable)\b/i,
        /\b(?:is|are|was|were)\s+structurally\s+\w+/i,
        /\b(?:safe|unsafe)\s+to\s+occupy\b/i,
        /\buninhabitable\b/i,
        /\bload[\s-]bearing\s+capacity\b/i,
        /\bload\s+capacity\s+(?:is|was)\b/i,
        /\bdesign\s+(?:is\s+)?adequa(?:te|cy)\b/i,
        /\bwill\s+(?:fail|collapse)\b/i,
        /\bimminent\s+(?:failure|collapse)\b/i
      ],
      zh: [
        /结构(?:上)?(?:安全|稳固|牢固|可靠|稳定)(?!隐患|问题|风险|疑虑|方面|性)/,
        /结构(?:上)?(?:不安全|不稳定|没有问题|无问题|合格|不合格)/,
        /(?:可以|适合|不宜|不适合)(?:安全)?(?:入住|居住)/,
        /承重(?:能力|力)(?:足够|不足|充足|满足|不满足)/,
        /(?:会|将)(?:倒塌|坍塌|垮塌|断裂|失效)/
      ]
    }
  ),
  phrase(
    'P-06',
    'Identification of a hazardous substance, or a pest / WDI determination',
    ['NY-ENV-01', 'NY-ANC-02', 'NY-LIC-10'],
    ['197-5.16(c)', '197-5.16(k)(9)', '§444-b(4)'],
    BOTH,
    {
      en: [
        /\b(?:is|are|was|were|confirmed\s+as|identified\s+as|tested\s+positive\s+for)\s+(?:asbestos|mold|mould|lead[\s-]based\s+paint|lead\s+paint)\b/i,
        // "the material contains asbestos" is a determination; "a material that may contain asbestos"
        // is the allowed replacement. The hedge in front is matched and excluded explicitly instead
        // of with a look-behind, which is not available on every WebKit version this app runs on.
        /(?:^|[.!?;:,]\s*|\b(?!may\b|might\b|could\b|possibly\b|potentially\b)\w+\s+)contains?\s+(?:asbestos|lead)\b/i,
        /\basbestos[\s-]containing\b/i,
        /\b(?:toxic|black)\s+mou?ld\b/i,
        /\bmou?ld\s+(?:is|was)\s+(?:present|confirmed|identified)\b/i,
        /\b(?:confirmed|identified|active)\s+(?:termite|pest|wood[\s-]destroying\s+insect)s?\s*(?:infestation|activity)?\b/i,
        /\b(?:termite|pest|wood[\s-]destroying\s+insect)s?\s+infestation\s+(?:is\s+)?(?:confirmed|present|identified)\b/i,
        /\bno\s+(?:termites?|pests?|infestation|wood[\s-]destroying\s+insects?)\b(?!\s+(?:evidence|signs?|indications?|activity))/i,
        /\b(?:termite|pest|insect)[\s-]free\b/i
      ],
      zh: [
        /(?:确认|确定|证实|检测出|化验(?:为|出))(?:为|是|有)?(?:石棉|铅漆|含铅油漆|霉菌|黑霉|白蚁)/,
        // 同上：「可能含有石棉」「疑似含有石棉」是允许的措辞，这里排除紧接其前的推测词尾字。
        /(?:^|[^能似])含有?(?:石棉|铅漆)/,
        /(?:有毒|黑色)霉菌/,
        /白蚁(?:侵害|感染|活动)(?:已)?(?:确认|证实)/,
        /(?:没有|无|不存在)(?:白蚁|虫害|蛀虫)(?!迹象|痕迹|活动迹象)/
      ]
    }
  ),
  phrase(
    'P-07',
    'Remaining life, cause, cost, method of correction or future condition',
    ['NY-EXC-03', 'NY-CODE-01'],
    ['197-5.16(k)(2)', '197-5.16(k)(4)', '197-5.16(k)(5)', '197-5.16(k)(6)', '197-5.16(k)(12)'],
    BOTH,
    {
      en: [
        /\bremaining\s+(?:useful\s+)?(?:service\s+)?life\b/i,
        /\bexpected\s+to\s+last\b/i,
        /\blast\s+(?:another|about|approximately|roughly)\s+\d+\s*(?:-|to)?\s*\d*\s*years?\b/i,
        /\b\d+\s*(?:-|to)\s*\d+\s+years?\s+(?:of\s+)?(?:remaining|life\s+left|useful\s+life)\b/i,
        /\b(?:cost|costs|price|estimate)\s+(?:of\s+)?(?:about|approximately|around|roughly)?\s*\$\s?\d/i,
        /\$\s?\d[\d,]*(?:\.\d+)?/,
        /\bwill\s+need\s+(?:to\s+be\s+)?(?:replaced|replacing|replacement)\s+(?:in|within)\s+\d+/i
      ],
      zh: [
        /剩余(?:使用)?寿命|使用寿命(?:还)?(?:剩|约)/,
        /(?:还能|可再|预计(?:还)?(?:能|可))(?:使用|用)\s*\d+\s*年/,
        /(?:费用|造价|维修费|更换费用)(?:约|大约|估计|预计|为)\s*[$＄]?\s*\d/,
        /(?:约|大约|大概|估计|预计)\s*[$＄]?\s*\d[\d,，]*\s*(?:美元|美金|元|刀)/
      ]
    }
  ),
  phrase(
    'P-08',
    'A conclusion about a concealed or not-observed component',
    ['PP-16', 'NY-LIM-01'],
    ['197-5.16(a)', '197-5.16(j)', '197-5.16(k)(1)'],
    BOTH,
    {
      en: [
        /\b(?:under|underneath|behind|beneath)\s+(?:the\s+)?(?:panels?|array|solar\s+panels?|finish(?:es)?|drywall|insulation|carpet|flooring|wall(?:s)?|siding|cladding)[^.;!?]{0,40}?\b(?:is|are|was|were|appears?|appeared|looks?|remains?)\s+(?:fine|ok|okay|good|sound|intact|satisfactory|in\s+good\s+condition|undamaged|dry)\b/i,
        /\bno\s+(?:issues?|problems?|damage|defects?|concerns?)\s+(?:behind|under|underneath|beneath)\b/i,
        /\b(?:appears?|appeared|looks?)\s+(?:fine|good|sound|intact)\s+(?:under|underneath|behind|beneath)\b/i
      ],
      zh: [
        /(?:太阳能板|面板|阵列|饰面|装修|保温层|墙面|地板|地毯|石膏板)(?:下(?:方|面)?|后(?:面|方)?|内部)[^，。；;,.!?]{0,12}?(?:没有问题|无问题|状况良好|完好|正常|没问题|干燥)/,
        /(?:被遮挡|未观察)(?:的)?部位[^，。；;,.!?]{0,8}?(?:没有问题|无问题|正常|完好)/
      ]
    }
  ),
  phrase(
    'P-09',
    'Absence or a pass derived from not seeing something in the photographs',
    ['PP-19', 'NY-SOP-02'],
    ['197-5.3(a)'],
    AI_ONLY,
    {
      en: [
        // What a photograph cannot establish: upstream protection, a device in another room, a
        // concealed component. "No smoke alarm was visible in this room" survives — it describes
        // what the photograph shows (OWNER-CONTRACT §4.8: AI not seeing ≠ absent).
        /\b(?:gfci|afci|arc[\s-]fault|ground[\s-]fault)\s+(?:protection\s+)?(?:is|are|was|were)\s+not\s+(?:present|installed|provided)\b/i,
        /\bno\s+(?:gfci|afci|arc[\s-]fault|ground[\s-]fault)\s+protection\b/i,
        /\b(?:smoke|carbon\s+monoxide|co)\s+(?:alarms?|detectors?)\s+(?:is|are|was|were)\s+not\s+(?:present|installed)\b/i,
        /\bthere\s+(?:is|are)\s+no\s+[\w\s-]{1,30}\s+(?:installed|present)\b/i,
        /\bnothing\s+(?:appears\s+)?(?:wrong|defective)\b/i,
        /\bno\s+(?:defects?|issues?|problems?)\s+(?:were\s+)?(?:present|found|observed)\s+anywhere\b/i
      ],
      zh: [/(?:未|没有)安装\s*(?:GFCI|AFCI|漏电保护|烟感|烟雾报警|一氧化碳)/i, /(?:不存在|没有)(?:任何)?(?:问题|缺陷|异常)/]
    }
  ),
  phrase(
    'P-10',
    'Water potability, or a gas / carbon-monoxide determination',
    ['NY-ENV-03', 'NY-TOOL-02'],
    ['197-5.8(c)(6)', '197-5.10(b)(12)'],
    AI_ONLY,
    {
      en: [
        /\b(?:is|are|was|were|not)\s+(?:potable|drinkable|safe\s+to\s+drink)\b/i,
        /\b(?:confirmed|detected|there\s+is\s+an?)\s+gas\s+leak\b/i,
        /\bgas\s+(?:is\s+)?leaking\b/i,
        /\bcarbon\s+monoxide\s+(?:is\s+)?(?:present|detected|leaking|elevated)\b/i
      ],
      zh: [/水质(?:合格|不合格|安全|不安全)|(?:可以|不能|不可)饮用/, /(?:确认|存在|正在)(?:燃气|煤气|天然气)泄漏/, /一氧化碳(?:超标|泄漏|存在)/]
    }
  ),
  phrase(
    'P-11',
    'Performance, adequacy, efficiency or capacity of a system',
    ['NY-EXC-06', 'NY-EXC-07'],
    ['197-5.16(k)(3)', '197-5.10(b)(9)', '197-5.11(b)(4)'],
    AI_ONLY,
    {
      en: [/\b(?:undersized|oversized|under-sized|over-sized)\b/i, /\b(?:inadequate|insufficient)\s+capacity\b/i, /\binefficient\b/i, /\befficiency\s+(?:is\s+)?(?:low|poor|inadequate)\b/i, /\bnot\s+(?:powerful|large)\s+enough\b/i],
      zh: [/(?:容量|功率|能力)(?:不足|偏小|过小|过大)/, /(?:效率|能效)(?:低|不高|低下|差)/]
    }
  ),
  phrase(
    'P-12',
    'Warranty, guarantee, certification or "passed"',
    ['NY-PRO-05', 'NY-PRO-09'],
    ['197-5.16(l)', '197-4.9(d)'],
    BOTH,
    {
      en: [
        /\bpassed\s+(?:the\s+)?inspection\b/i,
        /\b(?:is|are|was|were|been)\s+certified\b/i,
        /\bcertif(?:y|ies|ied)\s+(?:that|as)\b/i,
        /\bguarantee[sd]?\b/i,
        /\bwarrant(?:y|ies|ed)\b/i,
        /\bwe\s+assure\b/i
      ],
      zh: [/通过(?:了)?(?:验屋|检查|验收|检测)/, /验收合格|检验合格|合格证/, /(?:本报告|验屋师|我们)(?:保证|担保)/, /(?:提供|附带)(?:保修|质保|担保)/]
    }
  ),
  phrase(
    'P-13',
    'Statements about the client, the transaction or third parties',
    ['NY-PRO-06'],
    ['197-4.3', '§444-g(3)'],
    AI_ONLY,
    {
      en: [/\b(?:the\s+)?(?:buyers?|sellers?|purchasers?|listing\s+agent|realtors?|real\s+estate\s+agents?)\b/i, /\bpurchase\s+price\b/i, /\bnegotiat(?:e|es|ed|ing|ion|ions)\b/i, /\bbefore\s+closing\b/i],
      zh: [/买家|买方|卖家|卖方|中介|经纪人/, /成交价|议价|砍价|过户前/]
    }
  ),
  phrase(
    'P-14',
    'Reproduction, translation or paraphrase of the mandatory agreement clauses or other legal text',
    ['NY-PRO-05'],
    ['197-4.2(a)'],
    AI_ONLY,
    {
      en: [
        /\blicensed\s+by\s+the\s+nys\s+department\s+of\s+state\b/i,
        /\bhome\s+inspectors\s+may\s+only\s+report\s+on\s+readily\s+accessible\s+and\s+observed\s+conditions\b/i,
        /\barticle\s+12\s*-?\s*b\s+of\s+the\s+real\s+property\s+law\b/i,
        /\bcode\s+of\s+ethics\s+and\s+regulations\s+and\s+the\s+standards\s+of\s+practice\b/i,
        /\bclient\s+hereby\s+consents\s+to\s+allow\s+the\s+home\s+inspector\s+to\s+disclose\b/i
      ],
      zh: [/由纽约州州务(?:院|厅)(?:发|颁)?(?:照|证)/, /本人(?:特此)?同意(?:验屋师)?(?:向)?(?:业主|房主)披露/]
    }
  ),
  // Owner ratification 2026-09-13 (TW-5): a bedroom window is a supplemental observation — never an egress-
  // compliance determination either way.
  phrase(
    'P-15',
    'Egress-window code compliance or non-compliance (meets / fails egress requirements)',
    ['NY-PRO-02', 'NY-CODE-01'],
    ['197-4.4(d)', '197-5.16(e)'],
    BOTH,
    {
      en: [
        /\b(?:non[\s-]?)?compliant\s+egress\b/i,
        /\begress\s+(?:window\s+)?(?:is|was|are|were)\s+(?:not\s+)?(?:code[\s-])?compliant\b/i,
        /\b(?:meets?|met|fails?|failed|satisf(?:y|ies|ied)|does\s+not\s+meet|did\s+not\s+meet)\s+(?:the\s+)?(?:\w+\s+){0,2}egress\s+(?:code|codes|requirements?|standards?)\b/i,
        /\begress\s+(?:code|requirement)s?\s+(?:is|are|was|were)\s+(?:not\s+)?met\b/i
      ],
      zh: [/(?:不)?符合.{0,8}逃生(?:窗|出口)?(?:法规|规范|要求|标准)/, /逃生(?:窗|出口)?.{0,4}(?:不)?(?:符合|达标|合规)/]
    }
  ),
  // Owner ratification 2026-09-13 (TW-4): a recirculating range hood is a configuration, never a deficiency.
  phrase(
    'P-16',
    'A recirculating range hood written as a deficiency or as needing exterior venting',
    ['NY-PRO-02'],
    ['197-5.12(a)(4)', '197-5.12(b)(7)'],
    BOTH,
    {
      en: [
        /\brecirculating\b[^.;\n]{0,80}\b(?:must|should|needs?\s+to|required\s+to|has\s+to|have\s+to)\s+(?:be\s+)?(?:vent|duct|exhaust)/i,
        /\brecirculating\s+(?:range\s+)?hoods?\b[^.;\n]{0,40}\b(?:is|was|are|were)\s+(?:a\s+|an\s+)?(?:defect(?:ive)?|deficien(?:t|cy)|hazard(?:ous)?|improper|a\s+violation)\b/i
      ],
      zh: [/循环式.{0,20}(?:建议|应该|应|必须|需要|须).{0,12}(?:改为|改成|接到|排到|通到|引到).{0,6}室外/, /循环式.{0,16}(?:判|记|列|计|视)为(?:缺陷|隐患|违规)/]
    }
  )
]);

const BY_ID: ReadonlyMap<ProhibitedPhraseId, ProhibitedPhrase> = new Map(NY_PROHIBITED_PHRASES.map((p) => [p.id, p] as const));

export function prohibitedPhrase(id: ProhibitedPhraseId): ProhibitedPhrase {
  const found = BY_ID.get(id);
  if (!found) throw new Error(`unknown prohibited phrase ${id}`);
  return found;
}

export function isProhibitedPhraseId(value: unknown): value is ProhibitedPhraseId {
  return typeof value === 'string' && BY_ID.has(value as ProhibitedPhraseId);
}

export function prohibitedPhrasesFor(surface: WordingSurface): readonly ProhibitedPhrase[] {
  return NY_PROHIBITED_PHRASES.filter((p) => p.appliesTo.includes(surface));
}
